import { I18n } from "i18n-js";
import { TranslateOptions } from "i18n-js/src/typing";
import { UserState } from "store/user-store";
import { StringKey } from "./keys";
import setPrimeLocales from "./primeLocale";

const LangApi = () => import("api/LangApi");

const NONE_LOCALE = "none";

// Handler names
const TRANSLATION_MISSING = "traslation-missing";
const INTERPOLATED_MISSING = "interpolated-missing";
const NONE_MISSING = "none";

// regex for {key} placeholders
const STRING_PLACEHOLDER = /\{([^}]+)}/gm;

export enum AvailableLanguages {
  EN = "en",
  LT = "lt",
  PL = "pl",
}

const i18n = new I18n();

i18n.missingBehavior = TRANSLATION_MISSING;
i18n.enableFallback = true;
i18n.defaultLocale = "en";

i18n.missingTranslation.register(TRANSLATION_MISSING, (i18nUsed, scope) => {
  const scopeStr = Array.isArray(scope) ? scope.join(".") : scope;
  if (isTranslateMode()) {
    return `{{${scopeStr}}}`;
  }
  return `Err: ${scopeStr}`;
});

i18n.missingTranslation.register(INTERPOLATED_MISSING, (i18nUsed, scope) => {
  const scopeStr = Array.isArray(scope) ? scope.join(".") : scope;
  if (isTranslateMode()) {
    return `{{${scopeStr}}}`;
  }
  return `${scopeStr}`;
});

i18n.missingTranslation.register(NONE_MISSING, () => {
  return "";
});

// load current locale lang bundle
async function loadTranslations(companyRef: string) {
  if (isTranslateMode()) {
    console.log("Translate mode enabled, skipping loading translations");
    return;
  }

  const { default: langApi } = await LangApi();

  let response;
  if (companyRef !== undefined) {
    response = await langApi.getLanguageBundleCombined(i18n.locale, companyRef);
  } else {
    response = await langApi.getLangJson(i18n.locale);
  }

  if (response.data) {
    const localeDict = { [i18n.locale]: response.data };
    i18n.store(localeDict);
    console.log(`Loaded locale ${i18n.locale} bundle`);
  }
}

export async function setupLocale(state: UserState) {
  i18n.locale = state.getLanguage();

  setPrimeLocales();  
  // sometimes companyRef is not set, because of borked keycloak groups
  const companyRef = state?.companyContext?.companyReference ?? state.currentUser?.companyRef!!;

  await loadTranslations(companyRef);
}

export function isTranslateMode() {
  return i18n.locale === NONE_LOCALE;
}

/*
    Translates language key to current locale
 */
export function T(key: Readonly<StringKey>, options?: TranslateOptions) {
  return i18n.t(key, options);
}

/*
 * Interpolates(translates) string with placeholders
 */
export function I(message: string) {
  const matches = message.match(STRING_PLACEHOLDER);

  if (!matches) {
    return message;
  }

  while (matches.length) {
    const placeholder = matches.shift() as string;
    const name = placeholder.replace(STRING_PLACEHOLDER, "$1");

    const translatedPlaceholder = i18n.t(name, { missingBehavior: INTERPOLATED_MISSING });
    const value = translatedPlaceholder.toString().replace(/\$/gm, "_#$#_");

    const regex = new RegExp(placeholder.replace(/\{/gm, "\\{").replace(/}/gm, "\\}"), "g");

    message = message.replace(regex, value);
  }

  return message.replace(/_#\$#_/g, "$");
}
